<template>
  <div class="dashboard-view">
    <section class="dashboard-section">
      <div class="dashboard-section-title">Resumo</div>
      <div class="indicators-grid">
        <IndicatorCard
          v-for="indicator in mainIndicators"
          :key="indicator.key"
          v-bind="indicator"
        />
      </div>
    </section>

    <section class="dashboard-section">
      <div class="dashboard-agent-detail">
        <div class="dashboard-agent-detail-title">
          <v-icon size="15" color="#8a929b">mdi-source-branch</v-icon>
          Detalhamento · {{ agentName }}
        </div>
        <div class="azul-paineis">
          <AzulPanel
            etapa="completa"
            :base="base"
            :base-label="baseLabel"
            :range="range"
            :outro-ocupado="ocupado.respostas"
            @run-start="ocupado.completa = true"
            @run-end="ocupado.completa = false"
            @changed="loadIndicators"
          />
          <AzulPanel
            etapa="respostas"
            :base="base"
            :base-label="baseLabel"
            :range="range"
            :outro-ocupado="ocupado.completa"
            @run-start="ocupado.respostas = true"
            @run-end="ocupado.respostas = false"
            @changed="loadIndicators"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getAzulIndicators } from "@/services/azulApi.js";
import IndicatorCard from "@/views/Dashboard/Partials/IndicatorCard/IndicatorCard.vue";
import AzulPanel from "./AzulPanel.vue";

const EMPTY = {
  pendencias: 0,
  pendenciasRastreadas: 0,
  pendenciasNoPhoenix: 0,
  pendenciasFinalizadasAgente: 0,
  pendenciasAgente: 0,
  pendenciasManual: 0,
  pendenciasResolvidas: 0,
  pendenciasDetectadas: 0,
  recebidas: 0,
  importadas: 0,
  drive: 0,
  execucoesConferidas: 0,
  execucoesSemFoto: 0,
  enviadas: 0,
  jaExistiam: 0,
  semPendencia: 0,
  resolvidasReimport: 0,
  chegaramComPendencia: 0,
  chegaramAindaPendentes: 0,
  erroImportacao: 0,
  canceladasNaoImportadas: 0,
  naoVerificadas: 0,
  puladas: 0,
  semEmail: 0,
  aguardandoDecisao: 0,
  atencao: 0,
  erros: 0,
};

export default {
  name: "AzulDashboard",
  components: { IndicatorCard, AzulPanel },
  props: {
    range: { type: Object, required: true },
    agentName: { type: String, default: "Azul" },
    base: { type: String, required: true },
    baseLabel: { type: String, default: "" },
  },
  data() {
    // Qual painel esta com uma execucao desta base em andamento (para o outro nao disparar junto).
    return { metrics: EMPTY, ocupado: { completa: false, respostas: false } };
  },
  computed: {
    // Mesmos cartoes (IndicatorCard) do resumo da CVC, com os numeros da Azul.
    // Mesmos cartoes (IndicatorCard) do resumo da CVC, com os numeros do fluxo da Azul:
    // planilha (e-mail -> Drive) -> reservas enviadas ao Phoenix -> o que aconteceu com elas.
    mainIndicators() {
      const m = this.metrics;
      return [
        {
          key: "recebidas",
          label: "Planilhas recebidas por e-mail",
          value: m.recebidas,
          icon: "mdi-file-delimited-outline",
          color: "secondary",
          tooltipLines: [
            `${m.drive} salva(s) no Drive`,
            "Cada planilha conta uma vez, mesmo que o agente rode de novo no mesmo dia",
          ],
        },
        {
          key: "enviadas",
          label: "Reservas da planilha enviadas ao Phoenix",
          value: m.enviadas,
          icon: "mdi-send-outline",
          color: "primary",
          tooltipLines: this.enviadasTooltipLines,
        },
        {
          key: "semPendencia",
          label: "Reservas importadas no Phoenix sem pendência",
          value: m.semPendencia,
          icon: "mdi-check-circle-outline",
          color: "success",
        },
        {
          key: "resolvidas",
          label: "Reservas pendentes resolvidas",
          value: m.pendenciasResolvidas,
          icon: "mdi-check-all",
          color: "success",
          tooltipLines: m.pendenciasDetectadas
            ? [`${m.pendenciasResolvidas} de ${m.pendenciasDetectadas} reservas que tiveram pendência no período`]
            : [],
        },
        {
          key: "pendencias",
          label: "Reservas em pendência atualmente",
          value: m.pendencias,
          icon: "mdi-clipboard-alert-outline",
          color: "warning",
          tooltipLines: this.pendenciasTooltipLines,
        },
        {
          key: "erroImportacao",
          label: "Reservas com erro na importação",
          value: m.erroImportacao,
          icon: "mdi-alert-circle-outline",
          color: "error",
          tooltipLines: m.erroImportacao
            ? ["Reservas enviadas que não existem no Phoenix depois da importação"]
            : [],
        },
      ];
    },
    enviadasTooltipLines() {
      const m = this.metrics;
      if (!m.enviadas) return [];
      const lines = [
        `${m.jaExistiam} já existiam no Phoenix sem pendência`,
        `${m.semPendencia} entraram sem pendência`,
        `${m.resolvidasReimport || 0} estavam em pendência e foram resolvidas pela reimportação`,
        `${m.chegaramComPendencia} ficaram em pendência`,
        `${m.canceladasNaoImportadas} constam canceladas no Phoenix (não importadas)`,
        `${m.erroImportacao} com erro na importação`,
      ];
      if (m.naoVerificadas) lines.push(`${m.naoVerificadas} não verificada(s) (consulta ao Phoenix falhou)`);
      if (m.execucoesSemFoto) lines.push(`${m.execucoesSemFoto} execução(ões) sem conferência prévia: nelas nada é contado como "já existia"`);
      return lines;
    },
    // Hover do card "Reservas em pendencia atualmente" (valor = abertas AGORA). Mesmos numeros da aba Detalhamento.
    pendenciasTooltipLines() {
      const m = this.metrics;
      const lines = [];
      if (m.pendencias || m.pendenciasRastreadas) {
        lines.push(
          `${m.pendenciasNoPhoenix} em Pendência de Importação no Phoenix (${m.pendenciasManual} para correção manual · ${m.pendenciasAgente} na fila do agente)`
        );
        if (m.pendenciasFinalizadasAgente) {
          lines.push(`${m.pendenciasFinalizadasAgente} concluídas pelo agente com dados fictícios (a correção real continua pendente)`);
        }
        if (m.pendenciasResolvidas) lines.push(`${m.pendenciasResolvidas} resolvidas`);
        lines.push(`${m.pendenciasRastreadas} pendências rastreadas pelo agente no total`);
      }
      return lines;
    },
  },
  watch: {
    range() {
      this.loadIndicators();
    },
    base() {
      this.ocupado = { completa: false, respostas: false };
      this.loadIndicators();
    },
  },
  created() {
    this.loadIndicators();
  },
  methods: {
    async loadIndicators() {
      try {
        this.metrics = await getAzulIndicators(this.range, this.base);
      } catch (err) {
        console.error("Falha ao carregar os indicadores da Azul", err);
        this.metrics = EMPTY;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.azul-paineis > * + * {
  margin-top: 12px;
}
</style>

<!-- Mesmo CSS do Dashboard da CVC. -->
<style src="../Dashboard/styles.scss" scoped lang="scss" />
